import {IExceptionalService} from '../exceptionalService';

export abstract class ListController<T> {
    private _items: T[];
    private _selectedItem: T;
    private _searchTerm: string;
    private _previousSearchTerm: string;
    private _isLoading: boolean;
    private _isLoadingMore: boolean;
    private _isSearching: boolean;
    private _scrollToTop: boolean;
    private _isGlobalSearchAllowed: boolean = false;
    
    constructor(protected _$scope: angular.IScope,
        protected _$mdMedia: angular.material.IMedia,
        protected _$state: angular.ui.IStateService,
        protected _exceptionalService: IExceptionalService) {
    }

    $onInit() {
        this._$scope.$on('$stateChangeSuccess', () => this.updateSelectedItem());
    }

    get isLoading(): boolean {
        return this._isLoading;
    }

    set isLoading(value: boolean) {
        this._isLoading = value;
    }

    get isLoadingMore(): boolean {
        return this._isLoadingMore;
    }

    set isLoadingMore(value: boolean) {
        this._isLoadingMore = value;
    }

    get isSearching(): boolean {
        return this._isSearching;
    }

    set isSearching(value: boolean) {
        this._isSearching = value;
    }

    get items(): T[] {
        return this._items;
    }

    get selectedItem(): T {
        return this._selectedItem;
    }

    get searchTerm(): string {
        return this._searchTerm;
    }

    isSearchAllRequestsButtonVisible: boolean;
    isSearchAllRequestsButtonAllowedForUser : boolean ;

    set searchTerm(value: string) {
        this._previousSearchTerm = this._searchTerm;
        this._searchTerm = value;

        
        const isNumeric = /^[-+]?(\d+|\d+\.\d*|\d*\.\d+)$/;

        
        if (isNumeric.test(value) && this.isSearchAllRequestsButtonAllowedForUser) {
            this.isSearchAllRequestsButtonVisible = true;
            this.isGlobalSearchAllowed = false;
        } else {
            this.isSearchAllRequestsButtonVisible = false;
            this.isGlobalSearchAllowed = false;
        }
        
        if (this.isSearching)
            return;

        this._isSearching = true;
        this.goToList().then(
        () => {
            this.apply();
            return this.loadItems();
        },
        () => {
            this._searchTerm = this._previousSearchTerm;
            this.isSearching = false;
            this.apply();
        });
    }


    get isGlobalSearchAllowed(): boolean {
        return this._isGlobalSearchAllowed;
    }

    set isGlobalSearchAllowed(val: boolean) {
       this._isGlobalSearchAllowed = val;

        if (val === false)
            return;
        
        if (this.isSearching)
            return;

        this._isSearching = true;
        this.goToList().then(
            () => {
                this.apply();
                return this.loadItems();
            },
            () => {
                this.isSearching = false;
                this.apply();
            });
    }

    get scrollToTop(): boolean {
        return this._scrollToTop;
    }

    set scrollToTop(value: boolean) {
        this._scrollToTop = value;
    }

    updateListItem(item: T, index: number) {

        let itemsCopy = angular.copy(this.items);
        itemsCopy[index] = item;

        this._items = itemsCopy;
        this._selectedItem = this.findSelected(this.items);
    }

    updateSelectedItem() {
        if (this.shouldSetSelectedItem(this.items))
            this._selectedItem = this.findSelected(this.items);
    }

    loadMoreItems(): Promise<void> {

        if (this._isLoadingMore) {
            return Promise.resolve();
        }

        return new Promise<void>((resolve, reject) => {
            this._isLoadingMore = true;
            this.getMoreItems()
                .then(items => {
                    this.updateItems(items);
                    if (!this._selectedItem && this.shouldSetSelectedItem(items))
                        this._selectedItem = this.findSelected(items);
                    this.finishLoadingMore();
                    this.apply();
                })
                .then(() => resolve())
                .catch(() => reject());
        });
    }

    loadItems(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.isLoading = true;
            this.getItems()
                .then(items => {
                    this._items = items;
                    this.goToItemIfNeeded(items);
                    this.finishLoading();
                    this.apply();
                })
                .then(() => resolve())
                .catch(() => reject());
        });
    }

    goToList(): Promise<any> {
        return this.createGoToStatePromise(() => this.goToListState());
    }

    goToDetail(value: T): Promise<void> {
        if (!angular.isObject(value)) {
            return Promise.resolve();
        }

        return this.createGoToStatePromise(() => this.goToDetailState(value))
            .then(() => {
                this._selectedItem = value;
                this.apply();
            });
    }

   protected abstract getItems(): Promise<T[]>;

   protected abstract getMoreItems(): Promise<T[]>;

    protected abstract findSelected(items: T[]): T;

    protected abstract goToDetailState(item: T): angular.IPromise<any>;

    protected abstract goToListState(): angular.IPromise<any>;

    private goToItemIfNeeded(items): Promise<void> {
        if (!this.shouldSetSelectedItem(items)) {
            return Promise.resolve();
        }

        const item = this.findSelected(items);
        return this.goToDetail(item);
    }

    private finishLoading(): void {
        this.isLoading = false;
        this.isSearching = false;
        this.scrollToTop = true;
    }

    private finishLoadingMore(): void {
        this.isLoadingMore = false;
        this.scrollToTop = false;
    }

    private updateItems(items: T[]): void {
        const allItems = [];
        this._items.forEach(i => allItems.push(i));
        items.forEach(i => allItems.push(i));
        this._items = allItems;
    }

    rebindItemsToRefreshDetails(): void {
        const allItems = [];
        this._items.forEach(i => allItems.push(i));
        this._items = allItems;
        this.apply();
    }

    private apply(): void {
        this._$scope.$apply();
    }

    private shouldSetSelectedItem(items: T[]): boolean {

        return !this._exceptionalService.isErrorState()
            && !(this._$mdMedia('xs') || this._$mdMedia('sm'))
            && items && items.length > 0;
    }

    private createGoToStatePromise(goToStateFunction: () => angular.IPromise<any>): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            let unsubscribeSuccess;
            let unsubscribeError;
            const unsubscribe = () => {
                if (unsubscribeSuccess)
                    unsubscribeSuccess();

                if (unsubscribeError)
                    unsubscribeError();
            };
            unsubscribeSuccess = this._$scope.$on('$stateChangeSuccess', () => {
                unsubscribe();
                resolve();
            });

            unsubscribeError = this._$scope.$on('$stateChangeError', () => {
                unsubscribe();
                reject();
            });

            const previousState = this._$state.current;
            goToStateFunction().then((newState) => {
                if (previousState === newState) {
                    unsubscribe();
                    resolve();
                }
            });
        });
    }
}


/** WEBPACK FOOTER **
 ** ./DeliveryCreate/UI/app/core/list/listController.ts
 **/